/**
 * FacultyOnboardingPage - 4-step onboarding checklist for faculty
 * Steps: 1. Profile Photo, 2. Bio, 3. Stripe Connect (includes tax docs), 4. Show Certifications
 * Note: W-9 tax form collection is handled automatically by Stripe Connect Express
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FacultyLayout from './FacultyLayout';
import {
  getFacultyProfile,
  updateFacultyProfile,
  uploadHeadshot,
  getStripeOnboardingUrl,
  getStripeConnectStatus,
  getOnboardingProgress,
  type FacultyProfile,
  type OnboardingProgress,
} from '../../api/faculty';

type OnboardingStep = 'headshot' | 'bio' | 'stripe' | 'shows';

interface StepConfig {
  key: OnboardingStep;
  title: string;
  description: string;
  progressKey: keyof OnboardingProgress;
}

const STEPS: StepConfig[] = [
  {
    key: 'headshot',
    title: 'Profile Photo',
    description: 'Upload a professional headshot',
    progressKey: 'headshot_uploaded',
  },
  {
    key: 'bio',
    title: 'Bio & Credits',
    description: 'Tell us about your experience',
    progressKey: 'bio_completed',
  },
  {
    key: 'stripe',
    title: 'Payment & Tax Setup',
    description: 'Connect Stripe for payouts and tax documents',
    progressKey: 'stripe_connected',
  },
  {
    key: 'shows',
    title: 'Show Certifications',
    description: 'Review your certified shows',
    progressKey: 'shows_selected',
  },
];

export default function FacultyOnboardingPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile, setProfile] = useState<FacultyProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState<OnboardingStep>('headshot');
  const [stripeStatusMessage, setStripeStatusMessage] = useState<string | null>(null);
  const isInitialLoad = useRef(true);

  const loadProfile = useCallback(async () => {
    try {
      // Only show loading skeleton on initial load, not on refreshes
      if (isInitialLoad.current) {
        setIsLoading(true);
      }
      setError(null);
      const data = await getFacultyProfile();
      setProfile(data);

      // Find first incomplete step
      const firstIncomplete = STEPS.find(
        (step) => !data.onboarding_progress[step.progressKey]
      );
      if (firstIncomplete) {
        setActiveStep(firstIncomplete.key);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setIsLoading(false);
      isInitialLoad.current = false;
    }
  }, []);

  // Initial profile load
  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // Auto-check Stripe status when returning from Stripe onboarding
  useEffect(() => {
    const stripeParam = searchParams.get('stripe');
    if (stripeParam === 'complete') {
      // Remove the query param to prevent re-checking on refresh
      setSearchParams({}, { replace: true });

      // Check Stripe status after returning from Stripe
      const checkStripeStatus = async () => {
        try {
          setStripeStatusMessage('Checking Stripe status...');
          const status = await getStripeConnectStatus();
          if (status.payouts_enabled) {
            setStripeStatusMessage('Stripe setup complete! You\'re ready to receive payments.');
            await loadProfile();
          } else if (status.details_submitted) {
            setStripeStatusMessage(
              'Stripe setup submitted! Verification typically takes 1-3 business days.'
            );
            await loadProfile();
          } else {
            setStripeStatusMessage(
              'Stripe setup is still pending. It may take a few moments to update.'
            );
            await loadProfile();
          }
          // Clear message after 5 seconds
          setTimeout(() => setStripeStatusMessage(null), 5000);
        } catch {
          setStripeStatusMessage(
            'Could not verify Stripe status. Please click "Check Status" to retry.'
          );
          setTimeout(() => setStripeStatusMessage(null), 5000);
        }
      };

      checkStripeStatus();
    }
  }, [searchParams, setSearchParams, loadProfile]);

  const handleStepComplete = () => {
    loadProfile();
  };

  if (isLoading) {
    return (
      <FacultyLayout>
        <div className="max-w-3xl mx-auto">
          <div className="h-8 bg-warm-100 rounded w-1/3 animate-pulse mb-8" />
          <div className="card animate-pulse">
            <div className="h-40 bg-warm-100 rounded" />
          </div>
        </div>
      </FacultyLayout>
    );
  }

  if (error || !profile) {
    return (
      <FacultyLayout>
        <div className="max-w-3xl mx-auto">
          <div className="card text-center py-12">
            <p className="text-danger-600 mb-4">{error}</p>
            <button onClick={loadProfile} className="btn-primary">
              Try Again
            </button>
          </div>
        </div>
      </FacultyLayout>
    );
  }

  const progress = getOnboardingProgress(profile.onboarding_progress);
  const allComplete = progress === 100;

  return (
    <FacultyLayout>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900 mb-2">
            Complete Your Profile
          </h1>
          <p className="text-warm-600">
            {allComplete
              ? 'Your profile is complete! You can now receive class assignments.'
              : 'Complete these steps to start teaching masterclasses.'}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="card mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-warm-700">Overall Progress</span>
            <span className="text-sm font-medium text-primary-600">{progress}%</span>
          </div>
          <div className="h-3 bg-warm-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-500 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Stripe Status Message */}
        {stripeStatusMessage && (
          <div className="card mb-4 bg-primary-50 border-primary-200">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-600 border-t-transparent" />
              <span className="text-primary-700">{stripeStatusMessage}</span>
            </div>
          </div>
        )}

        {/* Steps */}
        <div className="space-y-4">
          {STEPS.map((step, index) => {
            const isComplete = profile.onboarding_progress[step.progressKey];
            const isActive = activeStep === step.key;

            return (
              <div
                key={step.key}
                className={`card cursor-pointer transition-all ${
                  isActive ? 'ring-2 ring-primary-500' : ''
                } ${isComplete ? 'bg-success-50 border-success-200' : ''}`}
                onClick={() => setActiveStep(step.key)}
              >
                <div className="flex items-start gap-4">
                  {/* Step Number / Check */}
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isComplete
                        ? 'bg-success-500 text-white'
                        : isActive
                        ? 'bg-primary-500 text-white'
                        : 'bg-warm-200 text-warm-600'
                    }`}
                  >
                    {isComplete ? (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <span className="font-bold">{index + 1}</span>
                    )}
                  </div>

                  {/* Step Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-warm-900">{step.title}</h3>
                      {isComplete && (
                        <span className="text-xs text-success-600 font-medium">Complete</span>
                      )}
                    </div>
                    <p className="text-sm text-warm-600">{step.description}</p>

                    {/* Expanded Step Content */}
                    {isActive && (
                      <div className="mt-4 pt-4 border-t border-warm-200">
                        <StepContent
                          step={step.key}
                          profile={profile}
                          onComplete={handleStepComplete}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Complete Button */}
        {allComplete && (
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/faculty')}
              className="btn-primary text-lg px-8 py-3"
            >
              Go to Dashboard
            </button>
          </div>
        )}
      </div>
    </FacultyLayout>
  );
}

/**
 * StepContent - Renders the content for each onboarding step
 */
interface StepContentProps {
  step: OnboardingStep;
  profile: FacultyProfile;
  onComplete: () => void;
}

function StepContent({ step, profile, onComplete }: StepContentProps) {
  switch (step) {
    case 'headshot':
      return <HeadshotStep profile={profile} onComplete={onComplete} />;
    case 'bio':
      return <BioStep profile={profile} onComplete={onComplete} />;
    case 'stripe':
      return <StripeStep profile={profile} onComplete={onComplete} />;
    case 'shows':
      return <ShowsStep profile={profile} onComplete={onComplete} />;
    default:
      return null;
  }
}

/**
 * HeadshotStep - Upload profile photo
 */
function HeadshotStep({ profile, onComplete }: { profile: FacultyProfile; onComplete: () => void }) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(profile.headshot_url);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target?.result as string);
    reader.readAsDataURL(file);

    // Upload
    try {
      setIsUploading(true);
      setError(null);
      await uploadHeadshot(file);
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div>
      <p className="text-sm text-warm-600 mb-4">
        Upload a professional headshot. This will be shown to studios when you're assigned to their class.
      </p>

      <div className="flex flex-col items-center sm:flex-row gap-4 sm:gap-6">
        {/* Preview */}
        <div className="w-24 h-24 rounded-full bg-warm-100 overflow-hidden flex items-center justify-center">
          {preview ? (
            <img src={preview} alt="Preview" className="w-full h-full object-cover" />
          ) : (
            <svg className="w-10 h-10 text-warm-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          )}
        </div>

        {/* Upload Button */}
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="btn-primary"
          >
            {isUploading ? 'Uploading...' : preview ? 'Change Photo' : 'Upload Photo'}
          </button>
          <p className="text-xs text-warm-500 mt-2">JPG or PNG, max 5MB</p>
        </div>
      </div>

      {error && <p className="text-sm text-danger-600 mt-2">{error}</p>}
    </div>
  );
}

/**
 * BioStep - Write bio and credits
 */
function BioStep({ profile, onComplete }: { profile: FacultyProfile; onComplete: () => void }) {
  const [bio, setBio] = useState(profile.bio || '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (bio.length < 100) {
      setError('Bio must be at least 100 characters');
      return;
    }
    if (bio.length > 2000) {
      setError('Bio must be under 2000 characters');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      await updateFacultyProfile({ bio });
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <p className="text-sm text-warm-600 mb-4">
        Write a bio that includes your Broadway credits and teaching experience. This will be shared with studios.
      </p>

      <textarea
        value={bio}
        onChange={(e) => setBio(e.target.value)}
        rows={6}
        placeholder="Enter bio here..."
        className="w-full px-4 py-3 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
      />

      <div className="flex items-center justify-between mt-2">
        <span className={`text-sm ${bio.length < 100 ? 'text-danger-600' : 'text-warm-500'}`}>
          {bio.length}/2000 characters (min 100)
        </span>
        <button
          onClick={handleSave}
          disabled={isSaving || bio.length < 100}
          className="btn-primary"
        >
          {isSaving ? 'Saving...' : 'Save Bio'}
        </button>
      </div>

      {error && <p className="text-sm text-danger-600 mt-2">{error}</p>}
    </div>
  );
}

/**
 * StripeStep - Connect Stripe for payouts and tax documents
 * Note: Stripe Connect Express automatically collects W-9 tax information
 */
function StripeStep({ profile, onComplete }: { profile: FacultyProfile; onComplete: () => void }) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stripeStatus, setStripeStatus] = useState<{
    details_submitted: boolean;
    payouts_enabled: boolean;
  } | null>(null);

  // Check status on mount to see if we're already connected
  // Only run once when component mounts and profile has stripe_account_id
  const hasCheckedRef = useRef(false);

  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    // Only check once, and only if not already marked as complete in onboarding_progress
    if (profile.stripe_account_id && !hasCheckedRef.current && !profile.onboarding_progress.stripe_connected) {
      hasCheckedRef.current = true;
      const checkInitialStatus = async () => {
        try {
          const status = await getStripeConnectStatus();
          setStripeStatus(status);
          // Auto-complete if details are submitted (user finished Stripe onboarding)
          if (status.details_submitted) {
            onCompleteRef.current();
          }
        } catch {
          // Silently fail - user can manually check status
        }
      };
      checkInitialStatus();
    }
  }, [profile.stripe_account_id, profile.onboarding_progress.stripe_connected]);

  const handleConnect = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const { url } = await getStripeOnboardingUrl();
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start Stripe setup');
      setIsLoading(false);
    }
  };

  const checkStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const status = await getStripeConnectStatus();
      setStripeStatus(status);
      // Consider connected if details_submitted is true (user completed Stripe onboarding)
      // In test mode, payouts_enabled might be false even after completing onboarding
      if (status.details_submitted) {
        onComplete();
      } else {
        setError('Stripe setup is not yet complete. Please finish the onboarding process.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check status');
    } finally {
      setIsLoading(false);
    }
  };

  // Show success if Stripe is enabled OR if details have been submitted (pending verification)
  const isStripeConnected =
    profile.stripe_account_status === 'enabled' ||
    (profile.stripe_account_id && stripeStatus?.details_submitted);

  if (isStripeConnected) {
    const isFullyEnabled = profile.stripe_account_status === 'enabled' && stripeStatus?.payouts_enabled;

    return (
      <div className={`border rounded-lg p-4 ${isFullyEnabled ? 'bg-success-50 border-success-200' : 'bg-primary-50 border-primary-200'}`}>
        <div className="flex items-center gap-3">
          <svg className={`w-5 h-5 ${isFullyEnabled ? 'text-success-600' : 'text-primary-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className={`font-medium ${isFullyEnabled ? 'text-success-700' : 'text-primary-700'}`}>
            {isFullyEnabled ? 'Stripe Connected' : 'Stripe Setup Complete'}
          </span>
        </div>
        <p className={`text-sm mt-2 ${isFullyEnabled ? 'text-success-600' : 'text-primary-600'}`}>
          {isFullyEnabled
            ? 'Your payment account is set up and ready to receive payouts.'
            : 'Your Stripe account has been set up. Payouts will be enabled once Stripe completes verification.'}
        </p>
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm text-warm-600 mb-4">
        Connect your bank account through Stripe to receive payments for your classes.
        Stripe will also securely collect your tax information (W-9) as part of this process.
      </p>

      <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-4">
        <h4 className="font-semibold text-primary-900 mb-2">What to expect</h4>
        <ul className="text-sm text-primary-800 space-y-1 list-disc list-inside">
          <li>Bank account or debit card for receiving payouts</li>
          <li>Tax information (SSN or EIN) for W-9 compliance</li>
          <li>Identity verification (quick and secure)</li>
        </ul>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={handleConnect}
          disabled={isLoading}
          className="btn-primary"
        >
          {isLoading ? 'Loading...' : profile.stripe_account_id ? 'Continue Setup' : 'Connect Stripe'}
        </button>

        {profile.stripe_account_id && (
          <button
            onClick={checkStatus}
            disabled={isLoading}
            className="px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50"
          >
            Check Status
          </button>
        )}
      </div>

      {profile.stripe_account_status === 'restricted' && !stripeStatus?.details_submitted && (
        <p className="text-sm text-warning-600 mt-2">
          Your Stripe account requires additional information. Click "Continue Setup" to complete.
        </p>
      )}

      {error && <p className="text-sm text-danger-600 mt-2">{error}</p>}
    </div>
  );
}

/**
 * ShowsStep - View certified shows (read-only, assigned by admin)
 */
function ShowsStep({ profile, onComplete }: { profile: FacultyProfile; onComplete: () => void }) {
  const shows = profile.shows || [];
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const hasAutoCompleted = useRef(false);

  useEffect(() => {
    // Auto-complete if shows are already assigned (only once)
    if (shows.length > 0 && !profile.onboarding_progress.shows_selected && !hasAutoCompleted.current) {
      hasAutoCompleted.current = true;
      onCompleteRef.current();
    }
  }, [shows, profile.onboarding_progress.shows_selected]);

  if (shows.length === 0) {
    return (
      <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
        <p className="text-sm text-warning-800">
          No shows have been assigned yet. An administrator will assign your certified shows based on your experience.
        </p>
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm text-warm-600 mb-4">
        You're certified to teach masterclasses for the following shows:
      </p>

      <div className="space-y-2">
        {shows.map((show) => (
          <div key={show.id} className="flex items-center gap-3 p-3 bg-warm-50 rounded-lg">
            <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
              {show.logo_url ? (
                <img src={show.logo_url} alt={show.name} className="w-full h-full object-cover rounded-lg" />
              ) : (
                <span className="text-primary-600 font-bold">{show.name.charAt(0)}</span>
              )}
            </div>
            <div>
              <p className="font-medium text-warm-900">{show.name}</p>
              <p className="text-xs text-warm-500">
                Certified {new Date(show.certified_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
